import type { SelectInputOption } from "../select-input/select-input";
import type {
  PdfCompressDocument,
  PdfCompressResponse,
  PdfCompressionType,
  PdfCustomCompressMode,
  PdfCompressionOrigin,
  PdfMergeDocument,
} from "./types";

export const COMPRESS_MAX_BYTES = 40 * 1024 * 1024;
/** Stable id so a later merge updates the same Compress row. */
export const MERGED_COMPRESS_ID = "compress-merged";

/** The response fields shown under a document after it compresses. */
export interface CompressResultSummary {
  compressedFileSize: number;
  compressionPercent: number;
  compressionTimeSeconds: number;
  targetMet: boolean;
}

export interface CompressEntry {
  id: string;
  origin: PdfCompressionOrigin;
  /** Changes when source bytes are replaced; asynchronous results must match it. */
  generation: number;
  name: string;
  fileName: string;
  file: File;
  /** PDF bytes the shared viewer renders. */
  pdf: ArrayBuffer;
  /** Upload bytes, kept so Redo can put the original document back. */
  originalPdf: ArrayBuffer;
  /** Compressed PDF from the last response, used to track download availability. */
  compressedPdf: ArrayBuffer | null;
  /** Bumped when `pdf` is replaced so the viewer reloads. */
  pdfRevision: number;
  /** Render version this row was copied from, when it came from Merge. */
  mergedFromVersion?: number;
  initialMergedTitle?: string;
  mergeDocuments?: PdfMergeDocument[];
  renderedMergeDocuments?: PdfMergeDocument[];
  title: string;
  compressionType: PdfCompressionType | "";
  customCompressMode: PdfCustomCompressMode | "";
  customCompressValue: number | null;
  /** Set from the compress response. Each document keeps its own. */
  result: CompressResultSummary | null;
}

export const COMPRESSION_TYPE_OPTIONS: SelectInputOption[] = [
  { value: "original", label: "Original" },
  { value: "standard", label: "Standard" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "custom", label: "Custom" },
];

/** Shared action eligibility for the workspace and its controller. */
export function canCompressEntry(
  entry: CompressEntry | undefined,
): entry is CompressEntry & { compressionType: PdfCompressionType } {
  if (!entry || entry.result || !entry.title.trim() ||
    entry.compressionType === "" || entry.compressionType === "original") return false;
  return entry.compressionType !== "custom" ||
    (entry.customCompressMode === "to_size" && entry.customCompressValue != null &&
      Number.isFinite(entry.customCompressValue) && entry.customCompressValue >= 0.01 &&
      entry.customCompressValue <= 40);
}

export function canDownloadEntry(entry: CompressEntry | undefined): boolean {
  return !!entry && (entry.compressionType === "original" || entry.compressedPdf != null);
}

export function toCompressDocuments(entries: CompressEntry[]): PdfCompressDocument[] {
  return entries.map(
    ({
      id,
      origin,
      name,
      fileName,
      file,
      title,
      compressionType,
      customCompressMode,
      customCompressValue,
    }) => ({
      id,
      origin,
      name,
      fileName,
      file,
      title,
      compressionType,
      customCompressMode,
      customCompressValue,
    }),
  );
}

export function pdfBytesToFile(bytes: ArrayBuffer, fileName: string): File {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(new Uint8Array(bytes));
  return new File([copy], fileName, { type: "application/pdf" });
}

/** Turns the compress response's `blob` into PDF bytes for the viewer. */
export async function pdfBytesFromCompressResponse(
  response: PdfCompressResponse,
): Promise<ArrayBuffer> {
  const { blob } = response;
  if (blob instanceof ArrayBuffer) return blob.slice(0);
  if (ArrayBuffer.isView(blob)) {
    const copy = new Uint8Array(blob.byteLength);
    copy.set(
      new Uint8Array(blob.buffer, blob.byteOffset, blob.byteLength),
    );
    return copy.buffer;
  }
  if (blob instanceof Blob) return blob.arrayBuffer();
  if (typeof blob === "string") {
    const trimmed = blob.trim();
    const payload = trimmed.includes(",")
      ? trimmed.slice(trimmed.indexOf(",") + 1)
      : trimmed;
    const base64 = payload.replace(/\s/g, "");
    if (!base64 || base64 === "***") {
      throw new Error("Compress response did not include a PDF.");
    }
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes.buffer;
  }
  throw new Error("Compress response did not include a PDF.");
}

/** Compact size for the PDF currently open in the viewer. */
export function formatByteSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    const kilobytes = bytes / 1024;
    return `${kilobytes >= 10 ? Math.round(kilobytes) : kilobytes.toFixed(1)} KB`;
  }
  const megabytes = bytes / (1024 * 1024);
  return `${megabytes >= 10 ? megabytes.toFixed(1) : megabytes.toFixed(2)} MB`;
}

export function formatCompressionPercent(percent: number): string {
  const rounded = Math.round(percent * 100) / 100;
  return `${rounded}%`;
}

export function formatCompressionSeconds(seconds: number): string {
  const rounded = Math.round(seconds * 100) / 100;
  return `${rounded} s`;
}
